import React, {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Model} from 'helpers/ui-components/Modal'
import {DiscountType} from 'models/OrderManagement'
import {formatNumberForDisplay} from 'utils/helper'
import {toast} from 'react-toastify'

type EditDiscountModalProps = {
 show: boolean
 discountType: DiscountType
 discountValue: number
 aggregatorDiscountAmount: number
 onClose: () => void
 onSave: (type: DiscountType, value: number) => void
}

const EditDiscountModal = ({
 show,
 discountType,
 discountValue,
 aggregatorDiscountAmount,
 onClose,
 onSave,
}: EditDiscountModalProps) => {
 const {t} = useTranslation()
 const [draftType, setDraftType] =
  useState<DiscountType>(discountType)
 const [draftValue, setDraftValue] = useState(
  String(discountValue)
 )

 useEffect(() => {
  if (show) {
   setDraftType(discountType)
   setDraftValue(String(discountValue))
  }
 }, [show, discountType, discountValue])

 const handleSave = () => {
  const parsed = parseFloat(draftValue)

  if (isNaN(parsed) || parsed < 0) {
   toast.error('Please enter a valid discount')
   return
  }

  if (draftType === 'PERCENTAGE' && parsed > 100) {
   toast.error('Percentage discount cannot exceed 100')
   return
  }

  onSave(draftType, parsed)
 }

 const typeButtonClass = (type: DiscountType) =>
  `flex-1 h-11 rounded-lg border text-sm font-medium transition-colors ${
   draftType === type
    ? 'border-[#4E83E4] text-[#4E83E4] bg-white'
    : 'border-[#D7DCE1] text-[#33393D] bg-white'
  }`

 return (
  <Model
   show={show}
   showHeader={false}
   closeBtnClass='absolute right-4 top-4 !bg-[#E6EBF0] !rounded-3xl w-7 h-7 !text-[#33393D]'
   header={''}
   close={onClose}
   modelClass='md:w-[480px]'
   modelContainerClass='!rounded-2xl'
   modelBodyClass='p-8 pt-10'
  >
   <div className='w-full'>
    <h2 className='text-[#171B1F] text-left font-bold text-2xl'>
     {t('orderManagement.editDiscountTitle')}
    </h2>
    <p className='text-[#80868C] text-sm mt-2 mb-6'>
     {t('orderManagement.editDiscountHelper')}
    </p>

    <p className='text-[#80868C] text-xs font-medium uppercase tracking-wide mb-2'>
     {t('orderManagement.discountType')}
    </p>
    <div className='flex gap-3 mb-6'>
     <button
      type='button'
      className={typeButtonClass('FLAT')}
      onClick={() => setDraftType('FLAT')}
     >
      ₹ {t('orderManagement.rupees')}
     </button>
     <button
      type='button'
      className={typeButtonClass('PERCENTAGE')}
      onClick={() => setDraftType('PERCENTAGE')}
     >
      % {t('orderManagement.percentage')}
     </button>
    </div>

    <p className='text-[#80868C] text-xs font-medium uppercase tracking-wide mb-2'>
     {t('orderManagement.discountValue')}
    </p>
    <div className='flex items-center h-12 rounded-lg bg-[#F5F8FA] px-4 mb-3'>
     <span className='text-[#33393D] text-sm font-medium mr-2'>
      {draftType === 'FLAT' ? '₹' : '%'}
     </span>
     <input
      type='text'
      inputMode='decimal'
      value={draftValue}
      onChange={(e) => setDraftValue(e.target.value)}
      className='flex-1 bg-transparent border-0 outline-none text-[#33393D] text-sm'
      autoFocus
     />
    </div>

    <p className='text-[#80868C] text-sm mb-8'>
     {t('orderManagement.aggregatorDiscountApplied', {
      value: formatNumberForDisplay(
       aggregatorDiscountAmount
      ),
     })}
    </p>

    <div className='flex justify-end gap-3'>
     <button
      type='button'
      onClick={onClose}
      className='h-10 px-5 rounded-lg border border-[#D7DCE1] bg-white text-[#80868C] text-sm font-medium'
     >
      {t('orderManagement.cancel')}
     </button>
     <button
      type='button'
      onClick={handleSave}
      className='h-10 px-5 rounded-lg bg-[#4E83E4] text-white text-sm font-medium'
     >
      {t('orderManagement.save')}
     </button>
    </div>
   </div>
  </Model>
 )
}

export default EditDiscountModal
